import React, { useState } from "react";
import { Modal, Button, Spinner } from "react-bootstrap";
import { PlusCircle } from "Components/Icons/PlusCircle";
import { TimesIcon } from "Components/Icons/TimesIcon";
import { useDomainCompetencies } from "@/hooks/useDomainCompetencies";

interface ModalUploadProps {
  show: boolean;
  onClose: () => void;
}

const ModalUpload = ({ show, onClose }: ModalUploadProps) => {
  const [file, setFile] = useState<File | null>(null);
  const [uploading, setUploading] = useState<boolean>(false);
  const [isDragOver, setIsDragOver] = useState<boolean>(false);
  const { uploadDomainCompetencyFile } = useDomainCompetencies();

  const handleFileChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    if (event.target.files && event.target.files.length > 0) {
      setFile(event.target.files[0]);
    }
  };

  const handleDragEnter = () => {
    setIsDragOver(true);
  };

  const handleDragLeave = () => {
    setIsDragOver(false);
  };

  const handleDrop = (event: React.DragEvent<HTMLDivElement>) => {
    event.preventDefault();
    if (event.dataTransfer.files && event.dataTransfer.files.length > 0) {
      setFile(event.dataTransfer.files[0]);
    }
    setIsDragOver(false);
  };

  const handleUpload = async () => {
    if (!file) {
      return;
    }

    setUploading(true);

    try {
      await uploadDomainCompetencyFile({ file });
    } finally {
      setUploading(false);
      onClose();
    }
  };

  const handleFileRemove = () => {
    setFile(null);
    setUploading(false);
  };

  const handleDragOver = (event: React.DragEvent<HTMLDivElement>) => {
    event.preventDefault();
    setIsDragOver(true);
  };

  return (
    <Modal show={show} onHide={onClose} centered className="modal-upload-container">
      <Modal.Body>
        <div className="modal-header">
          <PlusCircle className="plus-circle" />
          Add New Mapping Group
        </div>
        <p>
          Download the XML file, fill the template with the mapping group data and
          upload the file or drag it to this box to add it to the list.
        </p>
        <a className="download-template" href="/external/CompetencyImportTemplate.xlsx" download>
          Download template.XML
        </a>
        <div
          className={`form-file-container ${isDragOver ? "drag-over" : ""}`}
          onDrop={handleDrop}
          onDragOver={handleDragOver}
          onDragEnter={handleDragEnter}
          onDragLeave={handleDragLeave}
        >
          {!file && !uploading && (
            <>
              <p>Drop the file here to upload</p>
              <p>or</p>
              <label className="file-input-label">
                <input
                  type="file"
                  className="file-input"
                  onChange={handleFileChange}
                  disabled={uploading}
                />
                Select File
              </label>
            </>
          )}
          {uploading && (
            <div className="loading">
              <Spinner animation="border" />
            </div>
          )}
          {file && !uploading && (
            <div className="file-details">
              <span>{file.name}</span>
              <button type="button" className="remove-button" onClick={handleFileRemove}>
                <TimesIcon className="remove-button"/>
              </button>
            </div>
          )}
        </div>
        <div className="modal-footer">
            <Button
                variant="primary"
                onClick={handleUpload}
                disabled={!file || uploading}
            >
                Add Group
            </Button>
            <Button variant="secondary" onClick={onClose} disabled={uploading}>
                Cancel
            </Button>
        </div>
      </Modal.Body>
    </Modal>
  );
};

export default ModalUpload;
